import { useCallback, useState } from 'react'
import type { OnChangeFn, SortingState } from '@tanstack/react-table'

import { kDefaultDraftsSorting } from '@/drafting/destination/utils/drafts-list-view'

export interface UseDraftsListViewResult {
  searchQuery: string
  setSearchQuery: (query: string) => void
  clearSearch: () => void
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
}

/**
 * The Drafts list's search and sort state. Held outside the table so a control other than the column
 * headers can drive the same values; the table does the filtering and ordering.
 *
 * @param matterId - Both values reset when it changes: one route serves every matter, so the page
 *   stays mounted across a switch and a query typed for one would otherwise filter the next.
 */
export function useDraftsListView(matterId: string | null): UseDraftsListViewResult {
  const [searchQuery, setSearchQuery] = useState('')
  const [sorting, onSortingChange] = useState<SortingState>(kDefaultDraftsSorting)

  // Reset during render, not in an effect, so the new matter's first painted frame is unfiltered.
  // Latched in state rather than a ref: a ref mutated during a discarded render keeps its new value
  // while the queued reset is thrown away.
  const [latchedMatterId, setLatchedMatterId] = useState(matterId)
  if (latchedMatterId !== matterId) {
    setLatchedMatterId(matterId)
    setSearchQuery('')
    onSortingChange(kDefaultDraftsSorting)
  }

  const clearSearch = useCallback(() => setSearchQuery(''), [])

  return { searchQuery, setSearchQuery, clearSearch, sorting, onSortingChange }
}
